import { ANALYTICS_CONFIG, isAnalyticsConfigured } from '@/config/analytics';

// Google Analytics 4 Global Object
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Initialize Google Analytics 4
export const initializeGA4 = (): void => {
  if (!isAnalyticsConfigured()) {
    // GA4 not configured, skipping initialization (silent)
    return;
  }

  // Load GA4 script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${ANALYTICS_CONFIG.measurementId}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };

  // Configure GA4
  window.gtag('js', new Date());
  window.gtag('config', ANALYTICS_CONFIG.measurementId, {
    // Basic configuration
    page_title: document.title,
    page_location: window.location.href,
    page_referrer: document.referrer,
    
    // Custom parameters
    custom_map: {
      'custom_parameter_1': 'app_name',
      'custom_parameter_2': 'app_version',
      'custom_parameter_3': 'environment'
    },
    
    // Custom dimensions
    app_name: ANALYTICS_CONFIG.customParameters?.app_name,
    app_version: ANALYTICS_CONFIG.customParameters?.app_version,
    environment: ANALYTICS_CONFIG.customParameters?.environment,
    
    // Debug mode
    debug_mode: ANALYTICS_CONFIG.debugMode,
    
    // Enhanced measurement
    send_page_view: true,
    
    // Privacy settings
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false
  });

  // Removed console.logs for cleaner development experience
  // if (ANALYTICS_CONFIG.debugMode) {
  //   console.log('🚀 Google Analytics 4 initialized in debug mode');
  //   console.log('📊 Measurement ID:', ANALYTICS_CONFIG.measurementId);
  //   console.log('🔧 Debug mode:', ANALYTICS_CONFIG.debugMode);
  // }
};

// Check if GA4 is loaded
export const isGA4Loaded = (): boolean => {
  return typeof window !== 'undefined' && 
         typeof window.gtag === 'function' && 
         isAnalyticsConfigured();
};

// Safe gtag wrapper
export const safeGtag = (...args: any[]): void => {
  if (isGA4Loaded()) {
    window.gtag(...args);
  }
  // Removed console.log for cleaner development experience
  // else if (ANALYTICS_CONFIG.debugMode) {
  //   console.log('📊 GA4 Event (not loaded):', args);
  // }
};

// Page view tracking
export const trackPageView = (pageData?: {
  page_title?: string;
  page_location?: string;
  page_referrer?: string;
}): void => {
  const data = {
    page_title: pageData?.page_title || document.title,
    page_location: pageData?.page_location || window.location.href,
    page_referrer: pageData?.page_referrer || document.referrer
  };

  safeGtag('event', 'page_view', data);
  
  // Removed console.log for cleaner development experience
  // if (ANALYTICS_CONFIG.debugMode) {
  //   console.log('📄 Page View Tracked:', data);
  // }
};

// Custom event tracking
export const trackEvent = (
  eventName: string,
  parameters: Record<string, any> = {}
): void => {
  safeGtag('event', eventName, parameters);
  
  // Removed console.log for cleaner development experience
  // if (ANALYTICS_CONFIG.debugMode) {
  //   console.log('🎯 Event Tracked:', { eventName, parameters });
  // }
};

// User properties
export const setUserProperties = (properties: Record<string, any>): void => {
  safeGtag('set', 'user_properties', properties);
  
  // Removed console.log for cleaner development experience
  // if (ANALYTICS_CONFIG.debugMode) {
  //   console.log('👤 User Properties Set:', properties);
  // }
};

// User ID (for logged-in users)
export const setUserId = (userId: string): void => {
  safeGtag('set', 'user_id', userId);
  
  // Removed console.log for cleaner development experience
  // if (ANALYTICS_CONFIG.debugMode) {
  //   console.log('🆔 User ID Set:', userId);
  // }
};

// Export for use in components
export { ANALYTICS_CONFIG };